/**
 * O que passou para a dashboard v2 e, por isso, fica DESATIVADO aqui — desativado, e não removido.
 * O código das telas continua no repositório. Para religar uma delas, basta tirá-la desta lista.
 *
 * ─── A PÁGINA CONTINUA NO MENU ─────────────────────────────────────────────────────────────
 *
 * Quem abre uma página que migrou inteira encontra o aviso, com o link para a mesma tela na v2. Quem
 * abre uma página que migrou só em parte (o Buscar jogador) encontra o que ficou funcionando, e o
 * aviso no lugar do que saiu.
 *
 * ─── NÃO DESLIGAR PELO CARGO ───────────────────────────────────────────────────────────────
 *
 * O RTDB é o mesmo nas duas dashboards: tirar a permissão `find-guild` de um cargo esconde a tela da
 * v2 também.
 *
 * ─── O FRONT NÃO É A TRAVA ─────────────────────────────────────────────────────────────────
 *
 * As rotas da LAMBDA-dashboard-machine que só estas funções chamavam respondem 410
 * (`[MovedToDashboardV2]`). As compartilhadas com telas que não migraram — o envio de item da página
 * "Enviar itens", o ban dos scripts, o desban da fila sqs-unban — continuam ativas, e a função migrada
 * sai só daqui.
 */

export const DASHBOARD_V2_URL = "https://data.cuscuzes.com";

export type PageMovedToV2 = {
  /** O nome da página no menu. */
  title: string;
  /** O caminho da mesma página na v2. */
  v2Path: string;
};

/** Páginas que migraram inteiras, pela chave de permissão (a mesma nas duas dashboards). */
export const PAGES_MOVED_TO_V2: Record<string, PageMovedToV2> = {
  "clone-accounts": { title: "Clonar Contas", v2Path: "/app/operation/clone-accounts" },
  "find-guild": { title: "Buscar guild", v2Path: "/app/support/find-guild" },
  "rank-guilds": { title: "Rank de Guilds", v2Path: "/app/support/rank-guilds" },
  "item-tracking": { title: "Rastrear Itens", v2Path: "/app/support/item-tracking" },
  "incidents-tickets": { title: "Incidentes-Tickets", v2Path: "/app/support/incidents-tickets" },
  // A v2 não muda a cor do cargo nem dá ADM (este por decisão). Saiu mesmo assim porque esta tela,
  // ao salvar um cargo, regrava as permissões a partir do menu daqui e apaga as que só a v2 conhece
  // (silence-players, player-edit-*, sts-valve, access-*).
  "manage-users": { title: "Gerenciar Usuários", v2Path: "/app/developer/manage-users" },
  // Sem item no menu daqui: só a rota existia. Saiu quando o reinício foi ligado na v2.
  "manage-guilds": { title: "Gerenciar guildas", v2Path: "/app/support/manage-guilds" },
};

/** As abas do Buscar jogador da v2, pelo valor que o link dela entende em `?aba=`. */
export type FindPlayerV2Tab = "visao-geral" | "relacionadas" | "economia" | "itens" | "punicoes" | "email";

export const FIND_PLAYER_V2_TAB_LABELS: Record<FindPlayerV2Tab, string> = {
  "visao-geral": "Visão geral",
  relacionadas: "Relacionadas",
  economia: "Economia",
  itens: "Itens",
  punicoes: "Restrições",
  email: "E-mail",
};

/**
 * Painéis do Buscar jogador que migraram inteiros, pela `key` do painel, com a aba da v2 onde cada um
 * ficou.
 *
 * Ficam aqui, de fora desta lista, os que têm algo que a v2 não tem: Compras de NP (o exportar e o
 * "Gerar cobrança"), AccountOps, Backup da conta, Itens da conta, Twitch Drop Status e Inventário
 * Online. A busca também fica: os painéis que ficaram dependem dela, e a busca de vários nicks de uma
 * vez não existe na v2. E o botão de Gray List da própria conta, no topo: adicionar e remover a conta
 * aberta não foram para a v2.
 */
export const FIND_PLAYER_PANELS_MOVED_TO_V2: Record<string, FindPlayerV2Tab> = {
  // A Gray List das marcadas e o gráfico de ativações chegaram à v2 em 2026-10-07, e eram o que
  // segurava estes dois aqui.
  relatedAccounts: "relacionadas",
  playerNPTransactionsLogs: "economia",
  sendItem: "itens", // o CSV não vai para a v2, por decisão: envio em massa é da página "Enviar itens"
  manageBan: "punicoes",
  manageMute: "punicoes",
  sendCustomEmail: "email",
  checkItemSlot: "itens",
  loyaltyPath: "economia",
};

/** Partes de painéis que ficaram: o painel continua, e só a parte migrada sai. */
export const FIND_PLAYER_PARTS_MOVED_TO_V2 = {
  /**
   * Dados da conta: tudo — situação no jogo, senha, observações, informação pessoal, saldos,
   * aplicativo vinculado, aquisição —, menos a Twitch e as marcas de Gray List.
   */
  accountData: true,
  /**
   * Contas relacionadas: o ban em lote. O painel inteiro já está na lista de cima; isto vale se ele
   * voltar, porque o ban em lote continua na v2.
   */
  relatedAccountsBan: true,
};

/** Ferramentas de GM: só a aba de liberação de IP. Contas GM e Status de serviço não migraram. */
export const GM_TOOLS_IP_RELEASE_MOVED_TO_V2 = true;

export const dashboardV2Url = (path: string, params?: Record<string, string>): string => {
  const query = params ? new URLSearchParams(params).toString() : "";
  return `${DASHBOARD_V2_URL}${path}${query ? `?${query}` : ""}`;
};

/**
 * A conta aberta na aba certa do Buscar jogador da v2. `gameKey` é a chave que esta página já usa
 * (`global_prod`, `WydOrigem`), e a v2 entende o mesmo valor em `?servidor=`.
 */
export const findPlayerV2Url = (gameKey: string, playerId: string, tab: FindPlayerV2Tab): string =>
  dashboardV2Url("/app/support/find-player", {
    servidor: gameKey,
    conta: playerId,
    // A aba padrão da v2 não precisa ir no link — e a v2 ignora valor que não conhece.
    ...(tab !== "visao-geral" ? { aba: tab } : {}),
  });
